import { GetStaticProps, GetStaticPropsContext } from "next";
import Head from "next/head";
import AppRoot from "../../../app/app-component";
import { HomeRows } from "../../../app/models/home-rows";
import { Category, Product } from "../../../app/models/home-rows";
import { ProductList as ProductListResponse } from "../../../app/models/product-list";
import ProductList from "../../../app/modules/details/components/product-list/product-list";
import Seo from "../../../app/modules/shared/seo/seo";
import { API } from "../../../app/services/api-services";
import { ROUTES } from "../../../app/services/router-services";
import { createCnn2, options } from "../../../app/services/redis-services";
import {
  addBreadcrumbJsonLd,
  addCategoryJsonLd,
  addItemListJsonLd,
  addOrganizationJsonLd
} from "../../../app/services/schema-service";
import { replaceUnderscore } from "../../../app/services/text-services";
interface ProductListPageProps {
  productList: ProductListResponse;
  url: string;
}

function ProductListPage({ productList, url }: ProductListPageProps) {
  if (!url || !productList) return null;

  return (
    <AppRoot showHeader={true}>
      <Seo
        title={`تماشا و دانلود ${productList?.category?.title} ${productList.category.title_en} - دیجیتون`}
        description={productList?.category.description.slice(0, 150)}
        image={productList?.category?.feature_avatar?.hdpi}
        url={url}
        canonicalUrl={url}
        keywords={`${productList?.category?.title}, کارتون ${productList?.category.title_en}, انیمیشن ${productList?.category?.title}, دانلود ${productList?.category?.title}, دیجیتون, برنامه کودک, انیمیشن جدید,`}
      />
      <Head>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={addBreadcrumbJsonLd([
            {
              name: "خانه",
              url: API.site()
            },
            {
              name: "دسته بندی",
              url: API.site() + "category"
            },
            {
              name: productList?.category?.title ?? "",
              url: API.site() + url
            }
          ])}
          key="breadCrumbs-jsonld"
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={addCategoryJsonLd({
            url,
            itemListUrl: productList.products.map(product => {
              return API.site(true) + ROUTES.product(product);
            })
          })}
          key="itemList-jsonld"
        />
      </Head>
      <ProductList productList={productList} />
    </AppRoot>
  );
}

export const getStaticProps: GetStaticProps = async (
  context: GetStaticPropsContext
) => {
  const routeParam = context.params["category_id"] as string;
  const productId = +routeParam.split("-").reverse()[0];
  const product_list_limit =
    Number(process.env.NEXT_PUBLIC_PRODUCT_LIST_LIMIT) || 24;

  const REVALIDATE_PRODUCT =
    Number(process.env.NEXT_PUBLIC_REVALIDATE_PRODUCT) || 8640000;
  let client = null;
  try {
    const redis = require("redis");
    client = redis.createClient(options);
    await client.connect();
  } catch (e) {
    //console.log("could not access redis");
    client = null;
  }

  const cnn = createCnn2(client);
  const productList = await cnn.provideJson<ProductListResponse>(
    API.request_product_list(productId, product_list_limit + 1),
    REVALIDATE_PRODUCT
  );
  client && (await client.disconnect());

  if (!productList) return { notFound: true };

  const url = String(context.params["category_id"]);

  return {
    props: {
      productList: productList,
      url
    },
    revalidate: REVALIDATE_PRODUCT
  };
};

export const getStaticPaths = async () => {
  if (process.env.NEXT_PUBLIC_CACHE_PATHS !== "yes")
    return { paths: [], fallback: true };
  const allCatId = Number(process.env.NEXT_PUBLIC_ALL_CAT_ID) || 69;
  const home_item_limit =
    Number(process.env.NEXT_PUBLIC_HOME_ITEMS_LIMIT) || 10;
  const paths: { params: { category_id: string } }[] = [];
  // console.log("CAT_URL: ", API.request_all_categories);
  const categories = await API.fetchJson<Category[]>(
    API.request_category(allCatId)
  );
  // console.log(categories);

  // wait(5000);
  for (let i = 0; i < categories.length; i++) {
    const categori = categories[i];
    const category_id = getCatId(categori.title_en, categori.id);
    paths.push({ params: { category_id } });
  }

  const homeDetails = await API.fetchJson<HomeRows>(
    API.request_home_details(allCatId, 1, +home_item_limit)
  );

  const characters = homeDetails.data.headers.characters;
  for (let i = 0; i < characters.length; i++) {
    const character = characters[i];
    const category_id = getCatId(character.title_en, character.id);
    paths.push({ params: { category_id } });
  }

  return { paths, fallback: true };
};

export default ProductListPage;

const getCatId = (name: string, id: number) => {
  return `${replaceUnderscore(name, " ", "-")}-${id || "0"}`;
};

function wait(ms: number) {
  var start = new Date().getTime();
  var end = start;
  while (end < start + ms) {
    end = new Date().getTime();
  }
}
